import {
  Box,
  Button,
  Card,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ThemeProvider,
  Typography,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { pdf } from '@react-pdf/renderer';
import InvoicePDF from './TrackTraceReport';
import { downloadFile } from '../../../../helpers/downloadFile';
import { formatStandardDate } from '../../waterline/utils';
import { Invoice } from '../types';
import { StyledDialogFooter, StyledDialogTitle, StyledLargeDialogContent } from '../../../../components/Dialog.styled';
import { StyledTableRow } from '../../../../components/Table.styled';

type Props = {
  open: boolean;
  onClose: () => void;
  invoice: Invoice;
};

const TrackAndTraceModal: React.FC<Props> = ({ open, onClose, invoice }) => {
  const theme = useTheme();

  let buyerName = '';
  let buyerAddress = '';

  if (invoice?.BuyerAddress) {
    const { Name, Address1, City, State, ZipCode } = invoice.BuyerAddress;

    buyerName = Name;
    buyerAddress = `${Address1} ${City} ${State} ${ZipCode}`;
  }

  const leftInvoiceHeaders = [
    {
      name: 'Transaction Date:',
      value: formatStandardDate(invoice.TransactionDate)
    },
    {
      name: 'Shipment Date:',
      value: formatStandardDate(invoice.ShipmentDate)
    },
    {
      name: 'Invoice Number:',
      value: invoice.InvoiceNumber
    }
  ];

  const rightInvoiceHeaders = [
    {
      name: 'Seller Name:',
      value: invoice.SellerAddress.Name
    },
    {
      name: 'Seller Address:',
      value: `${invoice.SellerAddress.Address1}, ${invoice.SellerAddress.City}, ${invoice.SellerAddress.State} ${invoice.SellerAddress.ZipCode}`
    },
    {
      name: 'Buyer Name:',
      value: buyerName
    },
    {
      name: 'Buyer Address:',
      value: buyerAddress
    }
  ];

  const onExport = async () => {
    const renderedPdf = await pdf(<InvoicePDF invoice={invoice} />).toBlob();
    const url = URL.createObjectURL(renderedPdf);
    await downloadFile({
      url,
      isBlobURL: true,
      fileName: 'TrackandTraceReport.pdf'
    });

    onClose();
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog open={open} onClose={onClose} maxWidth="lg" sx={{ color: theme.palette.color50555b.main }} fullWidth>
        <StyledDialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Track and Trace Report</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </StyledDialogTitle>
        <StyledLargeDialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {leftInvoiceHeaders.map((row) => (
                <div key={row.name} className="f-s-14">
                  <strong className="bold">{row.name}</strong> {row.value}
                </div>
              ))}
            </Grid>
            <Grid item xs={12} sm={6}>
              {rightInvoiceHeaders.map((row) => (
                <div key={row.name} className="f-s-14">
                  <strong className="bold">{row.name}</strong> {row.value}
                </div>
              ))}
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Product Details:
          </Typography>
          <Card>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>NDC</TableCell>
                    <TableCell>Item Number</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Lot Number</TableCell>
                    <TableCell>Expiration Date</TableCell>
                    <TableCell>Strength</TableCell>
                    <TableCell>Dosage Form</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Unit of Measure</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Serial</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.ShippedItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        There are no products to display
                      </TableCell>
                    </TableRow>
                  ) : (
                    invoice.ShippedItems.map((item, index) => (
                      <StyledTableRow key={index}>
                        <TableCell>{item.Ndc}</TableCell>
                        <TableCell>{item.ItemNumber}</TableCell>
                        <TableCell>{item.Description}</TableCell>
                        <TableCell>{item.LotNumber}</TableCell>
                        <TableCell>{formatStandardDate(item.ExpirationDate)}</TableCell>
                        <TableCell>{item.Strength}</TableCell>
                        <TableCell>{item.DosageForm}</TableCell>
                        <TableCell>{item.NetContentDescription}</TableCell>
                        <TableCell>{item.UnitOfMeasure}</TableCell>
                        <TableCell>{item.Quantity}</TableCell>
                        <TableCell>{item.SerialNumber}</TableCell>
                      </StyledTableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </Card>
          <Divider sx={{ my: 2 }} />
          <div className="f-s-17 bold">Transaction Statement:</div>
          <div className="f-s-13">Seller has complied with each applicable subsection of FDCA Sec. 581(27)(A)-(G)</div>
        </StyledLargeDialogContent>
        <StyledDialogFooter>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={onExport} variant="contained">
            Export
          </Button>
        </StyledDialogFooter>
      </Dialog>
    </ThemeProvider>
  );
};

export default TrackAndTraceModal;
